import React, { useState, useEffect, useCallback } from "react";
import { Presentation, Keyframes, ArrowDown, ArrowUp, Refresh, MicrophoneMute, Microphone } from 'iconoir-react';
import { useTranslation } from '../i18n/useTranslation';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Updated to accept prompts as a prop and nodes to filter out existing prompts
export default function LeftSidebar({ prompts = [], nodes = [], toggleMuteAllPrompts }) {
  const t = useTranslation();
  const [showPrompts, setShowPrompts] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [promptDetails, setPromptDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  
  // Filter out prompts that already exist on the canvas
  useEffect(() => {
    const getFilteredPrompts = () => {
      if (!prompts || !nodes) return [];
      
      // Extract all prompt IDs that are already on the canvas
      // This is more comprehensive to catch all ways promptIds might be stored
      const existingPromptIds = nodes
        .filter(node => node.type === 'promptnode')
        .flatMap(node => {
          const ids = [];
          // Check all possible places where promptId might be stored
          if (node.data?.promptId) ids.push(node.data.promptId);
          if (node.id && node.id.startsWith('prompt_')) {
            const idPart = node.id.replace('prompt_', '');
            if (idPart) ids.push(idPart);
          }
          return ids;
        });
      
      // Return only prompts that are not already on the canvas
      const filtered = prompts.filter(prompt => {
        const promptId = prompt[1];
        // Convert both to strings for comparison to ensure consistent matching
        return !existingPromptIds.some(id => String(id) === String(promptId));
      });
      
      return filtered;
    };
    
    setFilteredPrompts(getFilteredPrompts());
  }, [prompts, nodes]);
  
  // Define fetchPromptDetails using useCallback with proper dependencies
  const fetchPromptDetails = useCallback(async () => {
    if (filteredPrompts.length === 0) return;
    
    setIsLoading(true);
    const details = {};
    
    // Fetch details for all prompts
    for (const prompt of filteredPrompts) {
      try {
        const promptId = prompt[1];
        const response = await fetch(`${API_BASE_URL}?${searchParams}&prompt_id=${promptId}`);
        const data = await response.json();
        
        if (data && data.nodes) {
          const promptNode = data.nodes.find(node => 
            node.id.toString() === promptId.toString()
          );
          
          if (promptNode) {
            const hasTools = promptNode.tools && promptNode.tools.length > 0;
            const toolCount = hasTools ? promptNode.tools.length : 0;
            
            details[promptId] = {
              hasTools,
              toolCount
            };
          }
        }
      } catch (error) {
        console.error("Error fetching prompt details:", error);
      }
    }
    
    setPromptDetails(details);
    setIsLoading(false);
  }, [filteredPrompts, searchParams]);
  
  // Only fetch details when expanding the prompts section
  useEffect(() => {
    if (showPrompts && filteredPrompts.length > 0 && Object.keys(promptDetails).length === 0) {
      fetchPromptDetails();
    }
  }, [showPrompts, filteredPrompts, fetchPromptDetails, promptDetails]);
  
  const onDragStart = (event, nodeType, data = null) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    
    // If this is an existing prompt, also transfer its data
    if (data) {
      event.dataTransfer.setData("application/prompt-data", JSON.stringify(data));
    }
    
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <aside className="fixed left-0 top-0 p-4 text-sm bg-gray-50 w-48 h-screen overflow-auto text-black">
      <div className="flex flex-col space-y-4">
        <h3 className="font-medium text-gray-700 text-xs mb-2">{t('nodeTypes')}</h3>
        
        {/* Node type buttons */}
        <div className="flex flex-col gap-3">
          <div
            className="bg-white p-3 border border-gray-200 rounded cursor-move flex items-center text-gray-500 hover:bg-gray-500 hover:text-white transition-colors duration-200"
            onDragStart={(event) => onDragStart(event, "promptnode")}
            draggable
          >
            <Presentation height={18} width={18} className="text-green-500 mr-2"/>
            <span className="text-xs">{t('promptNode.title')}</span>
          </div>
          
          
          <div
            className="bg-white p-3 border border-gray-200 rounded cursor-move flex items-center text-gray-500 hover:bg-gray-500 hover:text-white transition-colors duration-200"
            onDragStart={(event) => onDragStart(event, "intentnode")}
            draggable
          >
            <Keyframes height={18} width={18} className="text-blue-500 mr-2"/>
            <span className="text-xs">{t('intentNode.title')}</span>
          </div>
        </div>
        
        {/* Global Settings Section */}
        <div className="border-t border-gray-200 pt-3 mt-2">
          <button 
            className="w-full bg-gray-200 p-2 rounded flex items-center justify-between"
            onClick={() => setShowGlobalSettings(!showGlobalSettings)}
          >
            <span className="text-xs font-medium">
              {t('globalSettings')}
            </span>
            {showGlobalSettings ? 
              <ArrowUp height={14} width={14} /> : 
              <ArrowDown height={14} width={14} />
            }
          </button>
          
          {showGlobalSettings && (
            <div className="mt-2 flex flex-col gap-2">
              <button
                className="bg-white p-2 border border-gray-200 rounded cursor-pointer flex items-center text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-colors duration-200 text-xs"
                onClick={toggleMuteAllPrompts}
                title={t('toggleMuteAllPrompts')}
              >
                {nodes.filter(node => node.type === 'promptnode').some(node => node.data.muteUser) ? (
                  <>
                    <span className="bg-gray-200 rounded-full p-1 mr-2 flex items-center justify-center">
                      <Microphone height={18} width={18} />
                    </span>
                    <span>{t('unmuteAll')}</span>
                  </>
                ) : (
                  <>
                    <span className="bg-gray-200 rounded-full p-1 mr-2 flex items-center justify-center">
                      <MicrophoneMute height={18} width={18} />
                    </span>
                    <span>{t('muteAll')}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        {/* Separator */}
        {filteredPrompts && filteredPrompts.length > 0 && (
          <div className="border-t border-gray-200 pt-3 mt-2"></div>
        )}
        
        {/* Available prompts section */}
        {filteredPrompts && filteredPrompts.length > 0 && (
          <div>
            <button 
              className="w-full bg-gray-200 p-2 rounded flex items-center justify-between"
              onClick={() => setShowPrompts(!showPrompts)}
            >
              <span className="text-xs font-medium">
                {t('existingPrompts')}
              </span>
              {showPrompts ? 
                <ArrowUp height={14} width={14} /> : 
                <ArrowDown height={14} width={14} />
              }
            </button>
            
            {showPrompts && (
              <div className="mt-2 max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center p-4 text-gray-500">
                    <Refresh height={16} width={16} className="animate-spin mr-2" />
                    <span className="text-xs">{t('loadingPrompts')}</span>
                  </div>
                ) : (
                  filteredPrompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="bg-green-50 p-2 rounded mb-2 cursor-move border border-green-200 hover:bg-green-100"
                      onDragStart={(event) => onDragStart(event, "existingpromptnode", {
                        promptId: prompt[1],
                        promptName: prompt[0]
                      })}
                      draggable
                      title={`${prompt[0]}${promptDetails[prompt[1]]?.toolCount ? ` (${promptDetails[prompt[1]].toolCount} ${t('functionCount')})` : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center overflow-hidden">
                          <Presentation height={14} width={14} className="text-green-500 mr-1 flex-shrink-0" />
                          <span className="text-xs truncate">{prompt[0]}</span>
                        </div>
                        
                        {promptDetails[prompt[1]]?.hasTools && (
                          <div className="flex-shrink-0 bg-red-100 text-red-800 text-xs px-1.5 py-0.5 rounded-full ml-1">
                            {promptDetails[prompt[1]].toolCount}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
